import { Base } from '../../abstract/base.model';
import { isRecord } from '../utils/is-record';
import { nullableNumber, nullableString } from '../utils/wire-value-utils';
import { WorkOrderStatus } from './work-order-status.model';

/**
 * A work order, built from the hub's enriched work-order JSON (`user-work-orders-controller.ts`).
 *
 * Read-modify-write rule: writable fields the hub didn't send stay `undefined` (an explicit `null` stays
 * `null`). Display fallbacks belong in templates.
 */
export class WorkOrder extends Base {
  // Writable fields (PUT /work-orders/:id)
  title?: string | null;
  description?: string | null;
  assignedPilotProfileId?: string | null;
  customerId?: string | null;
  fieldId?: string | null;
  scheduledDate?: string | null;
  scheduledTime?: string | null;

  // Read-only fields
  organizationId?: string | null;
  jobId?: string | null;
  /** Hub-controlled lifecycle; defaults to 'open' only when the hub omits it. */
  status: WorkOrderStatus;
  startedAt?: string | null;
  completedAt?: string | null;
  cancelledAt?: string | null;
  cancellationReason?: string | null;
  /** From the linked job for marketplace work orders. */
  actualAcres?: number | null;
  /** The field's acreage. */
  estimatedAcres?: number | null;
  /** Set once the pilot logs a spray record from this work order. */
  sprayRecordId?: string | null;

  // Display fields the hub joins in (`WorkOrderService.enrichMany`)
  fieldName?: string | null;
  fieldAcreage?: number | null;
  cropType?: string | null;
  fieldCity?: string | null;
  fieldState?: string | null;
  fieldLatitude?: number | null;
  fieldLongitude?: number | null;
  customerName?: string | null;
  customerCity?: string | null;
  customerState?: string | null;
  assignedPilotName?: string | null;

  constructor(data: unknown) {
    super(data);
    const source = isRecord(data) ? data : {};
    this.title = nullableString(source['title']);
    this.description = nullableString(source['description']);
    this.assignedPilotProfileId = nullableString(source['assignedPilotProfileId']);
    this.customerId = nullableString(source['customerId']);
    this.fieldId = nullableString(source['fieldId']);
    this.scheduledDate = nullableString(source['scheduledDate']);
    this.scheduledTime = nullableString(source['scheduledTime']);
    this.organizationId = nullableString(source['organizationId']);
    this.jobId = nullableString(source['jobId']);
    this.status = (nullableString(source['status']) ?? 'open') as WorkOrderStatus;
    this.startedAt = nullableString(source['startedAt']);
    this.completedAt = nullableString(source['completedAt']);
    this.cancelledAt = nullableString(source['cancelledAt']);
    this.cancellationReason = nullableString(source['cancellationReason']);
    this.actualAcres = nullableNumber(source['actualAcres']);
    this.estimatedAcres = nullableNumber(source['estimatedAcres']);
    this.sprayRecordId = nullableString(source['sprayRecordId']);
    this.fieldName = nullableString(source['fieldName']);
    this.fieldAcreage = nullableNumber(source['fieldAcreage']);
    this.cropType = nullableString(source['cropType']);
    this.fieldCity = nullableString(source['fieldCity']);
    this.fieldState = nullableString(source['fieldState']);
    this.fieldLatitude = nullableNumber(source['fieldLatitude']);
    this.fieldLongitude = nullableNumber(source['fieldLongitude']);
    this.customerName = nullableString(source['customerName']);
    this.customerCity = nullableString(source['customerCity']);
    this.customerState = nullableString(source['customerState']);
    this.assignedPilotName = nullableString(source['assignedPilotName']);
  }
}
